import React, { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import tools from "../../../helpers/tools";
import Can from "../../common/Can/Can";
import { userAccessEnum } from "../../../config/accessEnum";
import StatusBadge from "../../common/StatusBadge/StatusBadge";
import RoleBadge from "../../common/RoleBadge";
import BrandLogo from "../Brand/BrandLogo";
import { buildDossierSummary } from "./dossierSummary";
import { formatPct, num } from "./DossierDashboard/formatters";
import styles from "./QuotationList.module.scss";

const FUEL_CLASS = {
  1: styles.fuelEss,
  2: styles.fuelDie,
  3: styles.fuelEle,
  4: styles.fuelHyb,
  5: styles.fuelDie, // Gazoil : même rendu que Diesel
};

const DossierCard = ({
  quotation,
  statusConstants,
  hideMargins,
  marginRateMin,
  onDelete,
}) => {
  const [expanded, setExpanded] = useState(false);
  const history = useHistory();

  const summary = buildDossierSummary(quotation.quotations);
  const isLow = (rate) => rate < marginRateMin + 1;

  const getStatusName = (statusId) => {
    const found = tools.findIn(statusConstants, "id", statusId + "");
    return found?.name || quotation.quotations.find(
      (q) => q.status?.id === statusId
    )?.status?.name || "";
  };

  // Regroupement par statut, comme l'ancien tableau
  const quotationsByStatus = quotation.quotations.reduce((acc, q) => {
    const id = q.status?.id || 1;
    if (!acc[id]) acc[id] = [];
    acc[id].push(q);
    return acc;
  }, {});

  const sortedLines = [...quotation.quotations].sort(
    (a, b) => (a.status?.id || 0) - (b.status?.id || 0)
  );

  const openDossier = () => history.push("/quotations/" + quotation.uuid);

  const stop = (e) => e.stopPropagation();

  return (
    <div className={styles.dossierCard} onClick={openDossier}>
      <div className={styles.dossierRow}>
        <button
          type="button"
          className={`${styles.chevBtn} ${expanded ? styles.chevOpen : ""}`}
          title={expanded ? "Replier les lignes" : "Voir les lignes"}
          aria-expanded={expanded}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          <i className={`fa fa-chevron-${expanded ? "up" : "down"}`} />
        </button>

        <div className={styles.idBlock}>
          <Link
            className={styles.refLink}
            to={"/quotations/" + quotation.uuid}
            onClick={stop}
          >
            {quotation.reference}
          </Link>
          <div className={styles.idSub}>
            {quotation.quotations.length}{" "}
            {tools.getStringPlural("ligne", quotation.quotations.length > 1)}
            {quotation.userGroup?.name ? " · " + quotation.userGroup.name : ""}
          </div>
        </div>

        <div className={styles.centerBlock}>
          <div className={styles.clientName}>
            {quotation.client.name}
            {Object.keys(quotationsByStatus).map((key) => (
              <StatusBadge
                key={"st" + key}
                statusId={parseInt(key)}
                statusName={`${quotationsByStatus[key].length} ${getStatusName(
                  parseInt(key)
                )}`}
              />
            ))}
          </div>
          <div className={styles.chips}>
            {summary.vehiclesLabel && (
              <span className={styles.chip}>
                <i className="fa fa-car" />
                {summary.vehiclesLabel}
              </span>
            )}
            {summary.durKmLabel && (
              <span className={styles.chip}>
                <i className="fa fa-tachometer-alt" />
                {summary.durKmLabel}
              </span>
            )}
            {summary.rentLabel && (
              <span className={styles.chip}>
                <i className="fa fa-euro-sign" />
                {summary.rentLabel}
              </span>
            )}
            {!hideMargins && summary.marginRate !== null && (
              <span
                className={
                  isLow(summary.marginRate) ? styles.marginLow : styles.marginOk
                }
              >
                marge {formatPct(summary.marginRate)}
              </span>
            )}
          </div>
        </div>

        <div className={styles.metaBlock}>
          {quotation.assignedTo ? (
            <div className={styles.metaAssigned}>
              {tools.getFullname(quotation.assignedTo)}{" "}
              <RoleBadge roleName={quotation.assignedTo.role?.name} />
            </div>
          ) : (
            <div className={styles.metaUnassigned}>Non assigné</div>
          )}
          <div className={styles.metaDates}>
            {quotation.createdBy && (
              <span title={"par " + tools.getFullname(quotation.createdBy)}>
                créé le {tools.formatDate(quotation.createdAt)}
              </span>
            )}
            {quotation.updatedBy && (
              <span title={"par " + tools.getFullname(quotation.updatedBy)}>
                {" "}
                · modifié le {tools.formatDate(quotation.updatedAt)}
              </span>
            )}
          </div>
        </div>

        <Can I={userAccessEnum.MODIFIER_DOSSIER}>
          <Link
            className={styles.openBtn}
            to={"/quotations/" + quotation.uuid}
            onClick={stop}
          >
            Ouvrir
          </Link>
        </Can>
        <Can I={userAccessEnum.SUPPRIMER_DOSSIER}>
          <button
            type="button"
            title="Supprimer ce dossier"
            className={styles.delBtn}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(quotation.uuid);
            }}
          >
            <i className="fa fa-times" />
          </button>
        </Can>
      </div>

      {expanded && (
        <div className={styles.lineDetail} onClick={stop}>
          <table className={styles.linesTable}>
            <thead>
              <tr>
                <th>Statut</th>
                <th>Véhicule</th>
                <th>Énergie</th>
                <th className={styles.colNum}>Qté</th>
                <th className={styles.colNum}>Loi de roulage</th>
                <th className={styles.colNum}>Loyer HT/mois</th>
                {!hideMargins && <th className={styles.colNum}>Marge</th>}
              </tr>
            </thead>
            <tbody>
              {sortedLines.map((q, k) => {
                const finish = q.vehicle?.brandModelFinish;
                const fuel = finish?.fuelType;
                const dur = num(q.dur);
                const km = num(q.km);
                const rent = num(q.totalRentHT);
                const rate = num(q.marginTTCRate);
                // Distingue « pas de donnée de marge » (cellule vide) d'une
                // marge réellement à 0 % (pastille rouge légitime).
                const hasRate =
                  q.marginTTCRate !== null &&
                  q.marginTTCRate !== undefined &&
                  q.marginTTCRate !== "";
                return (
                  <tr key={"line" + (q.uuid || k)}>
                    <td>
                      <StatusBadge
                        statusId={q.status?.id}
                        statusName={q.status?.name}
                      />
                    </td>
                    <td>
                      <div className={styles.vehicleCell}>
                        <BrandLogo brandName={q.vehicle?.brand?.name} />
                        <div>
                          <div className={styles.vehicleName}>
                            {q.vehicle?.brand?.name}{" "}
                            {q.vehicle?.brandModel?.name}
                          </div>
                          {finish?.name && (
                            <div className={styles.vehicleFinish}>
                              {finish.name}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {fuel?.name && (
                        <span className={FUEL_CLASS[fuel.id] || styles.fuel}>
                          {fuel.name}
                        </span>
                      )}
                    </td>
                    <td className={styles.colNum}>
                      ×{num(q.quantity) || 1}
                    </td>
                    <td className={styles.colNum}>
                      {dur > 0 && km > 0
                        ? dur +
                          " mois · " +
                          new Intl.NumberFormat("fr-FR").format(km) +
                          " km"
                        : ""}
                    </td>
                    <td className={`${styles.colNum} ${styles.rent}`}>
                      {rent > 0
                        ? new Intl.NumberFormat("fr-FR", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(rent) + " €"
                        : ""}
                    </td>
                    {!hideMargins && (
                      <td className={styles.colNum}>
                        {hasRate && (
                          <span
                            className={
                              isLow(rate) ? styles.marginLow : styles.marginOk
                            }
                          >
                            {formatPct(rate)}
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DossierCard;
